import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface AppLayoutContextValue {
  sideNavCollapsed: boolean;
  sideNavHidden: boolean;
  sideNavMobileOpen: boolean;
  footerCollapsed: boolean;
  chatPanelExpanded: boolean;
  toggleSideNavCollapsed: () => void;
  toggleSideNavHidden: () => void;
  toggleSideNavMobile: () => void;
  closeSideNavMobile: () => void;
  toggleFooterCollapsed: () => void;
  toggleChatPanelExpanded: () => void;
  expandChatPanel: () => void;
}

const AppLayoutContext = createContext<AppLayoutContextValue | undefined>(undefined);

const COLLAPSE_KEY = 'krysalis.sideNav.collapsed';
const HIDDEN_KEY = 'krysalis.sideNav.hidden';
const FOOTER_COLLAPSED_KEY = 'krysalis.footer.collapsed';
const CHAT_PANEL_KEY = 'krysalis.chatPanel.expanded';

function readFlag(key: string, defaultValue = false): boolean {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return defaultValue;
    return value === '1';
  } catch {
    return defaultValue;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export const AppLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sideNavCollapsed, setSideNavCollapsed] = useState(() => readFlag(COLLAPSE_KEY));
  const [sideNavHidden, setSideNavHidden] = useState(() => readFlag(HIDDEN_KEY));
  const [sideNavMobileOpen, setSideNavMobileOpen] = useState(false);
  const [footerCollapsed, setFooterCollapsed] = useState(() => readFlag(FOOTER_COLLAPSED_KEY, true));
  const [chatPanelExpanded, setChatPanelExpanded] = useState(() => readFlag(CHAT_PANEL_KEY, false));

  const toggleSideNavCollapsed = useCallback(() => {
    setSideNavCollapsed((prev) => {
      const next = !prev;
      writeFlag(COLLAPSE_KEY, next);
      return next;
    });
  }, []);

  const toggleSideNavHidden = useCallback(() => {
    setSideNavHidden((prev) => {
      const next = !prev;
      writeFlag(HIDDEN_KEY, next);
      if (!next) {
        setSideNavMobileOpen(false);
      }
      return next;
    });
  }, []);

  const toggleSideNavMobile = useCallback(() => {
    setSideNavMobileOpen((prev) => !prev);
  }, []);

  const closeSideNavMobile = useCallback(() => {
    setSideNavMobileOpen(false);
  }, []);

  const toggleFooterCollapsed = useCallback(() => {
    setFooterCollapsed((prev) => {
      const next = !prev;
      writeFlag(FOOTER_COLLAPSED_KEY, next);
      return next;
    });
  }, []);

  const toggleChatPanelExpanded = useCallback(() => {
    setChatPanelExpanded((prev) => {
      const next = !prev;
      writeFlag(CHAT_PANEL_KEY, next);
      return next;
    });
  }, []);

  const expandChatPanel = useCallback(() => {
    setChatPanelExpanded((prev) => {
      if (prev) return prev;
      writeFlag(CHAT_PANEL_KEY, true);
      return true;
    });
  }, []);

  const value = useMemo(
    () => ({
      sideNavCollapsed,
      sideNavHidden,
      sideNavMobileOpen,
      footerCollapsed,
      chatPanelExpanded,
      toggleSideNavCollapsed,
      toggleSideNavHidden,
      toggleSideNavMobile,
      closeSideNavMobile,
      toggleFooterCollapsed,
      toggleChatPanelExpanded,
      expandChatPanel,
    }),
    [
      sideNavCollapsed,
      sideNavHidden,
      sideNavMobileOpen,
      footerCollapsed,
      chatPanelExpanded,
      toggleSideNavCollapsed,
      toggleSideNavHidden,
      toggleSideNavMobile,
      closeSideNavMobile,
      toggleFooterCollapsed,
      toggleChatPanelExpanded,
      expandChatPanel,
    ]
  );

  return <AppLayoutContext.Provider value={value}>{children}</AppLayoutContext.Provider>;
};

export function useAppLayout(): AppLayoutContextValue {
  const ctx = useContext(AppLayoutContext);
  if (!ctx) {
    throw new Error('useAppLayout must be used within AppLayoutProvider');
  }
  return ctx;
}
